import { common, grey } from 'src/theme/palette';

export const widgetButtonStyle = {
    border: '1px solid',
    borderColor: 'divider',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 500,
    textTransform: 'none',
    color: grey[600],
    '&:hover': {
        borderColor: grey[400],
        backgroundColor: grey[50],
    },
};

export const widgetIconButtonStyle = {
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '8px',
    color: grey[600],
    padding: '6px',
    '&:hover': {
        borderColor: grey[400],
        backgroundColor: grey[50],
    },
    '@media (max-width: 600px)': {
        minWidth: '44px',
        minHeight: '44px',
    },
};

export const widgetCardStyle = {
    flex: '1 1 0',
    minWidth: '140px',
    backgroundColor: common.white,
    display: 'flex',
    flexFlow: 'column',
    padding: '16px',
    borderRadius: '12px',
    cursor: 'pointer',
    color: grey[800],
    border: `2px solid ${grey[200]}`,
    transition: 'all 0.2s ease',
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    '@media (max-width: 576px)': {
        flex: '1 1 calc(50% - 6px)',
        minWidth: 'unset',
    }
};

export const widgetCardWrapperStyle = {
    display: 'flex',
    width: '100%',
    gap: '12px',
    flexWrap: 'wrap',
    mb: 3,
};

export const widgetButtonWrapperStyle = {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '&.widgetButtonWrapper .widgetButton': {
        display: 'none'
    },
    '@media (max-width: 600px)': {
        width: '100%',
        justifyContent: 'flex-start',
    },
};

export const widgetTextStyle = {
    fontWeight: 400,
    fontSize: '12px',
    lineHeight: 1.3,
    textAlign: 'left',
    color: '#64748b',
};

export const widgetCounterTextStyle = {
    fontWeight: 700,
    fontSize: '28px',
    lineHeight: 1,
    color: '#1e293b',
};

export const widgetCounterWrapperStyle = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    mb: '4px',
};

export const widgetIconWrapper = {
    width: '32px', height: '32px', borderRadius: '8px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
};

export const widgetLoadingStyle = {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: "60px"
}

export const getWidgetAccentStyle = (color) => {
    if (!color) return {};
    return {
        borderColor: color,
        background: '#fff',
    };
};

// ── Urgency-based styling (matches Dashboard ActionCards) ──

export const WIDGET_URGENCY = {
    URGENT: 'urgent',
    WARNING: 'warning',
    GOOD: 'good',
    NEUTRAL: 'neutral',
};

/** Derive urgency from a raw count: 0 = good, 1-9 = warning, 10+ = urgent */
export const getUrgencyFromCount = (count) => {
    if (count >= 10) return WIDGET_URGENCY.URGENT;
    if (count > 0) return WIDGET_URGENCY.WARNING;
    return WIDGET_URGENCY.GOOD;
};

const URGENCY_COLORS = {
    [WIDGET_URGENCY.URGENT]: '#ef4444',
    [WIDGET_URGENCY.WARNING]: '#f59e0b',
    [WIDGET_URGENCY.GOOD]: '#10b981',
    [WIDGET_URGENCY.NEUTRAL]: grey[200],
};

// Darker text colors per urgency (readable on white backgrounds)
const URGENCY_TEXT_COLORS = {
    [WIDGET_URGENCY.URGENT]: '#dc2626',
    [WIDGET_URGENCY.WARNING]: '#b45309',
    [WIDGET_URGENCY.GOOD]: '#059669',
    [WIDGET_URGENCY.NEUTRAL]: '#475569',
};

// Light tint backgrounds for icon wrappers
const URGENCY_ICON_BG_COLORS = {
    [WIDGET_URGENCY.URGENT]: '#fef2f2',
    [WIDGET_URGENCY.WARNING]: '#fffbeb',
    [WIDGET_URGENCY.GOOD]: '#ecfdf5',
    [WIDGET_URGENCY.NEUTRAL]: '#f1f5f9',
};

/** Non-selected urgency: left accent bar is rendered as a positioned element; no border/bg override */
export const getWidgetUrgencyStyle = (urgency) => {
    if (!urgency || urgency === WIDGET_URGENCY.NEUTRAL) return {};
    return {};
};

/** Returns the accent bar color for a given urgency state */
export const getWidgetUrgencyBarColor = (urgency) =>
    URGENCY_COLORS[urgency] || 'transparent';

/** Returns the readable text color for a given urgency state */
export const getWidgetTextColor = (urgency) => URGENCY_TEXT_COLORS[urgency] || '#475569';

/** Returns the light tint background for icon wrappers */
export const getWidgetIconBgColor = (urgency) => URGENCY_ICON_BG_COLORS[urgency] || '#f1f5f9';

/** Badge style — small pill under the count text */
export const getWidgetBadgeStyle = (urgency) => {
    const base = {
        fontSize: '10px',
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: '10px',
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        alignSelf: 'flex-start',
        mt: '6px',
    };
    const map = {
        [WIDGET_URGENCY.URGENT]: { background: '#fee2e2', color: '#dc2626' },
        [WIDGET_URGENCY.WARNING]: { background: '#fef3c7', color: '#d97706' },
        [WIDGET_URGENCY.GOOD]: { background: '#dcfce7', color: '#059669' },
    };
    return { ...base, ...(map[urgency] || { background: '#f1f5f9', color: '#64748b' }) };
};

/** Small colored dot used inside badges */
export const widgetBadgeDotStyle = (color) => ({
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    display: 'inline-block',
    bgcolor: color || '#64748b',
    flexShrink: 0,
});
